'use client'
import type { PillarIconKey } from '@/lib/data/manifesto'

type IconProps = { active: boolean; id: string }

// viewBox 200×120 — content centred around (100, 60).

// ── SPEED · clock with orbital rings + glowing hands ─────────────────────
function SpeedIcon({ id }: IconProps) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-hidden>
      <defs>
        <linearGradient id={`${id}-amber`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fde68a" />
          <stop offset="100%" stopColor="#f97316" />
        </linearGradient>
        <radialGradient id={`${id}-bg`} cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
        </radialGradient>
        <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id={`${id}-soft`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="1.2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* centre ambient glow */}
      <circle cx={100} cy={60} r={50} fill={`url(#${id}-bg)`} />

      {/* orbit A — slow clockwise */}
      <g>
        <ellipse
          cx={100}
          cy={60}
          rx={70}
          ry={23}
          stroke="rgba(251,191,36,0.22)"
          strokeWidth={1}
          fill="none"
          transform="rotate(-25 100 60)"
        />
        <circle cx={170} cy={60} r={2.5} fill="#fbbf24" opacity={0.8} filter={`url(#${id}-soft)`} />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 100 60"
          to="360 100 60"
          dur="16s"
          repeatCount="indefinite"
        />
      </g>

      {/* orbit B — counter-clockwise */}
      <g>
        <ellipse
          cx={100}
          cy={60}
          rx={55}
          ry={18}
          stroke="rgba(96,165,250,0.18)"
          strokeWidth={1}
          fill="none"
          transform="rotate(20 100 60)"
        />
        <circle cx={155} cy={60} r={2} fill="#93c5fd" opacity={0.65} filter={`url(#${id}-soft)`} />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 100 60"
          to="-360 100 60"
          dur="22s"
          repeatCount="indefinite"
        />
      </g>

      {/* pulsing outer glow ring */}
      <circle
        cx={100}
        cy={60}
        r={38}
        stroke="#fbbf24"
        strokeWidth={6}
        fill="none"
        filter={`url(#${id}-glow)`}
      >
        <animate attributeName="opacity" values="0.15;0.4;0.15" dur="3s" repeatCount="indefinite" />
      </circle>

      {/* clock face */}
      <circle
        cx={100}
        cy={60}
        r={37}
        fill="rgba(5,9,26,0.92)"
        stroke="rgba(255,255,255,0.12)"
        strokeWidth={1}
      />

      {/* tick marks */}
      <g stroke="rgba(255,255,255,0.55)" strokeLinecap="round">
        <line x1={100} y1={25} x2={100} y2={30} strokeWidth={1.8} />
        <line x1={135} y1={60} x2={130} y2={60} strokeWidth={1.8} />
        <line x1={100} y1={95} x2={100} y2={90} strokeWidth={1.8} />
        <line x1={65} y1={60} x2={70} y2={60} strokeWidth={1.8} />
        <line x1={117.5} y1={29.7} x2={115.5} y2={34.1} strokeWidth={1} />
        <line x1={130.8} y1={42.5} x2={126.4} y2={44.5} strokeWidth={1} />
        <line x1={130.8} y1={77.5} x2={126.4} y2={75.5} strokeWidth={1} />
        <line x1={117.5} y1={90.3} x2={115.5} y2={85.9} strokeWidth={1} />
        <line x1={82.5} y1={90.3} x2={84.5} y2={85.9} strokeWidth={1} />
        <line x1={69.2} y1={77.5} x2={73.6} y2={75.5} strokeWidth={1} />
        <line x1={69.2} y1={42.5} x2={73.6} y2={44.5} strokeWidth={1} />
        <line x1={82.5} y1={29.7} x2={84.5} y2={34.1} strokeWidth={1} />
      </g>

      {/* hour hand */}
      <g>
        <line
          x1={100}
          y1={60}
          x2={100}
          y2={38}
          stroke={`url(#${id}-amber)`}
          strokeWidth={2.8}
          strokeLinecap="round"
          filter={`url(#${id}-soft)`}
        />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 100 60"
          to="360 100 60"
          dur="12s"
          repeatCount="indefinite"
        />
      </g>

      {/* minute hand */}
      <g>
        <line
          x1={100}
          y1={60}
          x2={100}
          y2={28}
          stroke="rgba(255,255,255,0.88)"
          strokeWidth={1.6}
          strokeLinecap="round"
        />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 100 60"
          to="360 100 60"
          dur="3s"
          repeatCount="indefinite"
        />
      </g>

      {/* second hand */}
      <g>
        <line
          x1={100}
          y1={70}
          x2={100}
          y2={26}
          stroke="#93c5fd"
          strokeWidth={0.9}
          strokeLinecap="round"
          opacity={0.7}
        />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 100 60"
          to="360 100 60"
          dur="1s"
          repeatCount="indefinite"
        />
      </g>

      {/* centre pin */}
      <circle cx={100} cy={60} r={3.5} fill={`url(#${id}-amber)`} filter={`url(#${id}-glow)`}>
        <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite" />
      </circle>
    </svg>
  )
}

// ── COST · bar chart with breathing bars + trend line + badge ────────────
function CostIcon({ id }: IconProps) {
  const baseY = 100
  const bars = [
    { x: 32, top: 80 },
    { x: 56, top: 65 },
    { x: 80, top: 48 },
    { x: 104, top: 30 },
    { x: 128, top: 12 },
  ]
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-hidden>
      <defs>
        <linearGradient id={`${id}-bar`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34d399" />
          <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.15" />
        </linearGradient>
        <linearGradient id={`${id}-line`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#34d399" />
          <stop offset="100%" stopColor="#22d3ee" />
        </linearGradient>
        <radialGradient id={`${id}-bg`} cx="75%" cy="20%" r="65%">
          <stop offset="0%" stopColor="#34d399" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
        </radialGradient>
        <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* ambient bg */}
      <circle cx={140} cy={20} r={75} fill={`url(#${id}-bg)`} />

      {/* faint grid */}
      <g stroke="rgba(255,255,255,0.05)" strokeWidth={1}>
        <line x1={20} y1={20} x2={180} y2={20} />
        <line x1={20} y1={40} x2={180} y2={40} />
        <line x1={20} y1={60} x2={180} y2={60} />
        <line x1={20} y1={80} x2={180} y2={80} />
      </g>

      {/* breathing bars — animate height + y in sync to anchor bottom */}
      {bars.map((b, i) => {
        const h = baseY - b.top
        const hMin = h * 0.65
        const yMin = baseY - hMin
        return (
          <rect
            key={i}
            x={b.x}
            y={b.top}
            width={18}
            height={h}
            rx={4}
            fill={`url(#${id}-bar)`}
            opacity={0.7 + i * 0.06}
            filter={`url(#${id}-glow)`}
          >
            <animate
              attributeName="height"
              values={`${h};${hMin};${h}`}
              dur="3.5s"
              repeatCount="indefinite"
              begin={`${i * 0.4}s`}
            />
            <animate
              attributeName="y"
              values={`${b.top};${yMin};${b.top}`}
              dur="3.5s"
              repeatCount="indefinite"
              begin={`${i * 0.4}s`}
            />
          </rect>
        )
      })}

      {/* trend line through bar tops */}
      <polyline
        points="41,80 65,65 89,48 113,30 137,12"
        stroke={`url(#${id}-line)`}
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={`url(#${id}-glow)`}
        opacity={0.95}
      />

      {/* pulsing endpoint dot */}
      <circle cx={137} cy={12} r={4} fill="#34d399" filter={`url(#${id}-glow)`}>
        <animate attributeName="opacity" values="0.6;1;0.6" dur="2.2s" repeatCount="indefinite" />
      </circle>

      {/* base axis */}
      <line x1={20} y1={100} x2={180} y2={100} stroke="rgba(255,255,255,0.18)" strokeWidth={1} />

      {/* +87% badge */}
      <g filter={`url(#${id}-glow)`}>
        <rect
          x={155}
          y={4}
          width={32}
          height={16}
          rx={8}
          fill="rgba(52,211,153,0.18)"
          stroke="#34d399"
          strokeWidth={1.2}
        />
        <text
          x={171}
          y={12}
          fontSize={8}
          fontWeight={900}
          fill="#34d399"
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="ui-sans-serif, system-ui, sans-serif"
        >
          +87%
        </text>
      </g>
    </svg>
  )
}

// ── NETWORK · hub with signals flowing along spokes ──────────────────────
function NetworkIcon({ id }: IconProps) {
  const hub = { x: 100, y: 60 }
  const nodes = [
    { x: 45, y: 28 },
    { x: 155, y: 30 },
    { x: 42, y: 90 },
    { x: 158, y: 92 },
  ]
  const extras = [
    { x: 100, y: 12, r: 4 },
    { x: 15, y: 60, r: 3 },
    { x: 185, y: 60, r: 3 },
  ]
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-hidden>
      <defs>
        <linearGradient id={`${id}-accent`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#e9d5ff" />
          <stop offset="100%" stopColor="#818cf8" />
        </linearGradient>
        <radialGradient id={`${id}-bg`} cx="30%" cy="20%" r="65%">
          <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
        </radialGradient>
        <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id={`${id}-soft`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="1.5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* ambient bg */}
      <circle cx={70} cy={30} r={80} fill={`url(#${id}-bg)`} />

      {/* rotating outer orbit */}
      <g>
        <circle
          cx={100}
          cy={60}
          r={48}
          stroke="rgba(167,139,250,0.1)"
          strokeWidth={1}
          fill="none"
        />
        <circle
          cx={148}
          cy={60}
          r={2.5}
          fill="#a78bfa"
          opacity={0.55}
          filter={`url(#${id}-soft)`}
        />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 100 60"
          to="360 100 60"
          dur="22s"
          repeatCount="indefinite"
        />
      </g>

      {/* main spokes hub → outer nodes + top */}
      <g stroke="rgba(167,139,250,0.22)" strokeWidth={1.5} strokeLinecap="round">
        {nodes.map((n, i) => (
          <line key={i} x1={hub.x} y1={hub.y} x2={n.x} y2={n.y} />
        ))}
        <line x1={hub.x} y1={hub.y} x2={extras[0].x} y2={extras[0].y} />
      </g>

      {/* extra thin satellite links */}
      <g stroke="rgba(99,102,241,0.18)" strokeWidth={1}>
        <line x1={nodes[0].x} y1={nodes[0].y} x2={extras[1].x} y2={extras[1].y} />
        <line x1={nodes[1].x} y1={nodes[1].y} x2={extras[2].x} y2={extras[2].y} />
        <line x1={nodes[2].x} y1={nodes[2].y} x2={extras[1].x} y2={extras[1].y} />
      </g>

      {/* signals flowing from hub along main spokes */}
      <circle r={2.5} fill="#c4b5fd" filter={`url(#${id}-soft)`} opacity={0.9}>
        <animateMotion
          dur="2s"
          repeatCount="indefinite"
          path={`M ${hub.x},${hub.y} L ${nodes[0].x},${nodes[0].y}`}
        />
      </circle>
      <circle r={2.2} fill="#a5b4fc" filter={`url(#${id}-soft)`} opacity={0.85}>
        <animateMotion
          dur="2.4s"
          repeatCount="indefinite"
          begin="0.9s"
          path={`M ${hub.x},${hub.y} L ${nodes[1].x},${nodes[1].y}`}
        />
      </circle>
      <circle r={2} fill="#c4b5fd" filter={`url(#${id}-soft)`} opacity={0.8}>
        <animateMotion
          dur="1.9s"
          repeatCount="indefinite"
          begin="1.5s"
          path={`M ${hub.x},${hub.y} L ${nodes[2].x},${nodes[2].y}`}
        />
      </circle>
      <circle r={2} fill="#a5b4fc" filter={`url(#${id}-soft)`} opacity={0.75}>
        <animateMotion
          dur="2.6s"
          repeatCount="indefinite"
          begin="0.4s"
          path={`M ${hub.x},${hub.y} L ${nodes[3].x},${nodes[3].y}`}
        />
      </circle>

      {/* outer nodes with pulsing radius + opacity */}
      {nodes.map((n, i) => (
        <circle
          key={i}
          cx={n.x}
          cy={n.y}
          r={5.5}
          fill={`url(#${id}-accent)`}
          filter={`url(#${id}-glow)`}
        >
          <animate
            attributeName="r"
            values="5;6.8;5"
            dur="2.1s"
            repeatCount="indefinite"
            begin={`${i * 0.55}s`}
          />
          <animate
            attributeName="opacity"
            values="0.8;1;0.8"
            dur="2.1s"
            repeatCount="indefinite"
            begin={`${i * 0.55}s`}
          />
        </circle>
      ))}

      {/* top + side accent dots */}
      {extras.map((n, i) => (
        <circle
          key={`e-${i}`}
          cx={n.x}
          cy={n.y}
          r={n.r}
          fill={i === 0 ? 'rgba(167,139,250,0.85)' : 'rgba(99,102,241,0.75)'}
          filter={`url(#${id}-soft)`}
        />
      ))}

      {/* centre hub outer ring */}
      <circle
        cx={hub.x}
        cy={hub.y}
        r={16}
        fill="rgba(11,9,26,0.85)"
        stroke={`url(#${id}-accent)`}
        strokeWidth={1.4}
      >
        <animate attributeName="opacity" values="0.5;1;0.5" dur="2.8s" repeatCount="indefinite" />
      </circle>

      {/* centre hub fill */}
      <circle cx={hub.x} cy={hub.y} r={10} fill={`url(#${id}-accent)`} filter={`url(#${id}-glow)`}>
        <animate attributeName="opacity" values="0.7;1;0.7" dur="2.2s" repeatCount="indefinite" />
      </circle>

      {/* checkmark in centre */}
      <polyline
        points="96,61 99,65 105,55"
        stroke="white"
        strokeWidth={1.8}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export const PILLAR_ICONS: Record<PillarIconKey, (p: IconProps) => React.JSX.Element> = {
  speed: SpeedIcon,
  cost: CostIcon,
  network: NetworkIcon,
}
